/**
 * Hash router (no packages). Routes look like `#/teacher/console/roster`. Hash routing works on Cloudflare
 * Pages without rewrite rules and keeps working from the iPad home screen. Nothing private goes in a route.
 *
 *   #/                     Landing
 *   #/join[/CODE]          Student join (class code -> name -> PIN)
 *   #/play[/tab]           Student app (lobby, quiz, orientation, game shell tabs)
 *   #/teacher              Teacher home (create a world or sign in)
 *   #/teacher/new          Create a world
 *   #/teacher/console[/tab] Teacher console
 *   #/teacher/cards        Printable join cards
 *   #/teacher/placement    Placement review
 *   #/projector            Projector "World Stage"
 *   #/solo                 Single player: save slots and a new game (engine runs in the browser)
 *   #/solo/new/SLOT        Pick a nation for a new solo game
 *   #/solo/SLOT[/tab]      A solo game (the same game shell tabs), #/solo/SLOT/debrief at the end
 */
import { useSyncExternalStore } from 'react';

export interface Route {
  /** Path segments, e.g. ['teacher', 'console', 'roster']. */
  parts: string[];
}

function parse(hash: string): Route {
  const path = hash.replace(/^#/, '').split('?')[0] ?? '';
  return { parts: path.split('/').filter(Boolean).map((p) => decodeURIComponent(p)) };
}

let current = typeof window !== 'undefined' ? parse(window.location.hash) : { parts: [] };
const listeners = new Set<() => void>();

if (typeof window !== 'undefined') {
  window.addEventListener('hashchange', () => {
    current = parse(window.location.hash);
    for (const l of [...listeners]) l();
    // Move focus to the new screen's main landmark for screen-reader users.
    requestAnimationFrame(() => {
      const main = document.getElementById('main');
      if (main && !main.contains(document.activeElement)) {
        if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
        main.focus({ preventScroll: true });
      }
    });
  });
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

const get = () => current;

export function useRoute(): Route {
  return useSyncExternalStore(subscribe, get, get);
}

export function href(...parts: string[]): string {
  return `#/${parts.map((p) => encodeURIComponent(p)).join('/')}`;
}

export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  const target = to.startsWith('#') ? to : `#${to.startsWith('/') ? to : `/${to}`}`;
  if (window.location.hash === target) return;
  if (opts.replace) {
    const url = `${window.location.pathname}${window.location.search}${target}`;
    window.history.replaceState(null, '', url);
    current = parse(target);
    for (const l of [...listeners]) l();
  } else {
    window.location.hash = target;
  }
  window.scrollTo(0, 0);
}
